import { AuthorizedContent, useAuthUser } from '@frontegg/react';
import { Box, MenuList } from '@mui/material';
import React, { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { TenantContext } from '../../../contexts/tenantContext';
import { colors, fontWeight, TypographyStyledGlobal } from '../../../theme';
import {
    ArrowDropDownIconStyled,
    ArrowDropUpIconStyled,
    DividerMenuStyled,
    MenuItemsStyled,
} from '../../../theme/style';
import RolesType from '../../../utils/enum/RolesType';
import useFetcher from '../../../utils/hooks/useFetcher';
import { ButtonDropdownStyled, MenuDropdownStyled } from '../DropdownTranslateMenu/styled';

interface TenantOption {
    id: number;
    code: string;
    label: string;
}

const DropdownTenantMenuContent: FC = () => {
    const context = useContext(TenantContext);
    const [openDropdown, setOpenDropdown] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const fetcher = useFetcher();
    const { data: tenants } = useSWR<TenantOption[]>([`${process.env.REACT_APP_API_URL}/tenant/`], fetcher);

    const [selectedSchema, setSelectedSchema] = context!;

    const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
        setOpenDropdown((prev) => !prev);
        setAnchorEl(event.currentTarget);
    };

    const handleSelect = (code: string) => {
        localStorage.setItem('lastSelectedTenant', code);
        setSelectedSchema(code);
        setOpenDropdown(false);
    };

    const selectedTenant = tenants?.find((t) => t.code === selectedSchema);

    return (
        <ButtonDropdownStyled
            endIcon={openDropdown ? <ArrowDropUpIconStyled /> : <ArrowDropDownIconStyled />}
            menuopen={openDropdown ? 1 : 0}
            onClick={handleToggle}
            disableRipple
        >
            <Box sx={{ display: 'flex', width: '100%' }}>
                <TypographyStyledGlobal color={colors.grey200} margin="auto auto auto 8px" weight="500">
                    {selectedTenant?.label ?? selectedSchema}
                </TypographyStyledGlobal>
            </Box>
            <MenuDropdownStyled anchorEl={anchorEl} open={openDropdown}>
                <MenuList sx={{ padding: 0 }}>
                    {tenants?.map((tenant) => (
                        <MenuItemsStyled
                            key={tenant.id}
                            $isSelected={tenant.code === selectedSchema}
                            onClick={() => handleSelect(tenant.code)}
                            disableRipple
                        >
                            <TypographyStyledGlobal textAlign="center" weight={fontWeight.semiRegular}>
                                {tenant.label}
                            </TypographyStyledGlobal>
                        </MenuItemsStyled>
                    ))}
                    <DividerMenuStyled sx={{ display: 'none' }} />
                </MenuList>
            </MenuDropdownStyled>
        </ButtonDropdownStyled>
    );
};

const DropdownTenantMenu: FC = () => {
    const user = useAuthUser();
    const location = useLocation();
    const { t } = useTranslation('navigation');
    const metadata = JSON.parse(user.metadata);

    if (metadata.database_schema !== 'askia') { return null; }
    if (location.pathname !== '/') { return null; }

    return (
        <AuthorizedContent requiredRoles={[RolesType.ADMIN, RolesType.ADMIN_MOAI]}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TypographyStyledGlobal color={colors.grey300} weight="500">
                    {t('selectClient')}
                </TypographyStyledGlobal>
                <DropdownTenantMenuContent />
            </Box>
        </AuthorizedContent>
    );
};

export default DropdownTenantMenu;
